import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Shield,
  AlertTriangle,
  Check,
  X,
  Eye,
  EyeOff,
  Loader,
  Lock,
  Database,
  Download,
  Trash2,
  Calendar,
  Globe,
  LogOut,
} from "lucide-react";
import { format } from "date-fns";
import BetaNavbar from "./components/BetaNavbar";
import Modal from "components/common/Modal";
import DatePicker from "components/common/DatePicker";
import betaAPI from "v2/api";
import showToast from "utils/toast";
import { Trans, useTranslation } from "react-i18next";
import LanguageSelect from "components/common/LanguageSelect";
import ConfirmDialog from "v2/views/components/common/ConfirmDialog";
import { clearSession } from "utils/authSession";

// Password validation helper
const validatePassword = (password) => {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasSymbol: /[!@#$%^&*()_+\-=[\]{}|;':",./<>?]/.test(password),
  };
};

const AccountSettings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Profile state
  const [profile, setProfile] = useState({
    id: "",
    email: "",
    fname: "",
    lname: "",
    is_superadmin: false,
  });
  const [originalProfile, setOriginalProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [editedName, setEditedName] = useState({ fname: "", lname: "" });
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  // Password state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Delete data state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Export data state
  const [exporting, setExporting] = useState(false);

  // Selective delete state
  const [showSelectiveDeleteModal, setShowSelectiveDeleteModal] =
    useState(false);
  const [selectiveDeleteMode, setSelectiveDeleteMode] = useState("all");
  const [selectiveDateFrom, setSelectiveDateFrom] = useState(null);
  const [selectiveDateTo, setSelectiveDateTo] = useState(null);
  const [selectiveDeleting, setSelectiveDeleting] = useState(false);

  // Password confirmation state (for sensitive operations)
  const [showConfirmPasswordModal, setShowConfirmPasswordModal] =
    useState(false);
  const [confirmPasswordValue, setConfirmPasswordValue] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [confirmingPassword, setConfirmingPassword] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'email' | 'password' | 'delete' | 'selective-delete'
  const [confirmationToken, setConfirmationToken] = useState(null);

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await betaAPI.getCurrentUser();
        setProfile(data);
        setOriginalProfile(data);
      } catch (err) {
        console.error("Failed to fetch profile:", err);
        showToast.error(t("settings.account.toasts.loadProfileFailed"));
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, []);

  // Check if profile name has changes
  const hasProfileChanges =
    originalProfile &&
    (editedName.fname !== originalProfile.fname ||
      editedName.lname !== originalProfile.lname);

  // Check if email has changes
  const hasEmailChanges = newEmail && newEmail !== originalProfile?.email;

  // Handle profile name save
  const handleSaveProfile = async () => {
    if (!hasProfileChanges) return;

    setProfileSaving(true);
    try {
      const { data } = await betaAPI.updateCurrentUser({
        body: {
          fname: editedName.fname,
          lname: editedName.lname,
        },
      });

      // Update localStorage user
      localStorage.setItem("user", JSON.stringify(data));

      setOriginalProfile(data);
      setProfile(data);
      setShowProfileForm(false);
      showToast.success(t("settings.account.toasts.profileUpdated"));
    } catch (err) {
      console.error("Failed to update profile:", err);
      showToast.error(t("settings.account.toasts.updateProfileFailed"));
    } finally {
      setProfileSaving(false);
    }
  };

  // Initiate email change - requires password confirmation first
  const handleInitiateEmailChange = () => {
    if (!hasEmailChanges) return;
    setPendingAction("email");
    setShowConfirmPasswordModal(true);
  };

  // Handle email change (called after password confirmation)
  const handleChangeEmail = async (token) => {
    if (!hasEmailChanges) return;

    setEmailSaving(true);
    try {
      const { data } = await betaAPI.updateCurrentUser({
        body: {
          email: newEmail,
          confirmation_token: token,
        },
      });

      // Update localStorage user
      localStorage.setItem("user", JSON.stringify(data));

      setOriginalProfile(data);
      setProfile(data);
      setNewEmail("");
      setShowEmailForm(false);
      setConfirmationToken(null);

      showToast.success(t("settings.account.toasts.emailUpdated"));
    } catch (err) {
      console.error("Failed to update email:", err);
      if (err.status === 409) {
        showToast.error(t("settings.account.toasts.emailInUse"));
      } else if (err.status === 403) {
        showToast.error(t("settings.account.toasts.confirmationExpired"));
        setConfirmationToken(null);
      } else {
        showToast.error(t("settings.account.toasts.updateEmailFailed"));
      }
    } finally {
      setEmailSaving(false);
    }
  };

  // Password validation
  const passwordValidation = validatePassword(newPassword);
  const isPasswordValid =
    passwordValidation.minLength &&
    passwordValidation.hasUppercase &&
    passwordValidation.hasSymbol &&
    newPassword === confirmPassword;

  // Initiate password change - requires password confirmation first
  const handleInitiatePasswordChange = () => {
    setPendingAction("password");
    setShowConfirmPasswordModal(true);
  };

  // Handle password change (called after password confirmation)
  const handleChangePassword = async (token) => {
    if (!isPasswordValid) return;

    setPasswordSaving(true);
    try {
      await betaAPI.changePassword({
        confirmationToken: token,
        newPassword,
      });

      showToast.success(t("settings.account.toasts.passwordChanged"));
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordForm(false);
      setConfirmationToken(null);
    } catch (err) {
      console.error("Failed to change password:", err);
      if (err.status === 403) {
        showToast.error(t("settings.account.toasts.confirmationExpired"));
        setConfirmationToken(null);
      } else {
        showToast.error(t("settings.account.toasts.changePasswordFailed"));
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  // Handle export data
  const [confirmSignOutEverywhere, setConfirmSignOutEverywhere] =
    useState(false);
  const handleSignOutEverywhere = async () => {
    setConfirmSignOutEverywhere(false);
    try {
      await betaAPI.signOutEverywhere();
    } catch (err) {
      console.error("Failed to sign out everywhere:", err);
      showToast.error(t("settings.account.sessions.failed"));
      return;
    }
    clearSession();
    navigate("/v2/auth/login", { replace: true });
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const { data } = await betaAPI.exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().split("T")[0];
      a.href = url;
      a.download = `sit-my-data-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast.success(t("settings.account.toasts.exportSuccess"));
    } catch (err) {
      console.error("Failed to export data:", err);
      showToast.error(t("settings.account.toasts.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  // Initiate selective delete - requires password confirmation first
  const handleInitiateSelectiveDelete = () => {
    setPendingAction("selective-delete");
    setShowConfirmPasswordModal(true);
  };

  // Handle selective delete (called after password confirmation)
  const handleSelectiveDelete = async (token) => {
    setSelectiveDeleting(true);
    try {
      const params = { confirmationToken: token, mode: selectiveDeleteMode };
      if (selectiveDeleteMode === "date_range") {
        params.dateFrom = format(selectiveDateFrom, "yyyy-MM-dd");
        params.dateTo = format(selectiveDateTo, "yyyy-MM-dd");
      }
      const { data } = await betaAPI.selectiveDeleteMyData(params);

      const summary = data?.summary;
      if (summary) {
        showToast.success(
          t("settings.account.toasts.dataClearedSummary", {
            memberships: summary.memberships_deleted || 0,
            logs: summary.activity_logs_anonymized || 0,
          })
        );
      } else {
        showToast.success(t("settings.account.toasts.dataCleared"));
      }
      setShowSelectiveDeleteModal(false);
      setSelectiveDeleteMode("all");
      setSelectiveDateFrom(null);
      setSelectiveDateTo(null);
      setConfirmationToken(null);
    } catch (err) {
      console.error("Failed to selectively delete data:", err);
      if (err.status === 403) {
        showToast.error(t("settings.account.toasts.confirmationExpired"));
        setConfirmationToken(null);
        setShowSelectiveDeleteModal(false);
      } else {
        showToast.error(t("settings.account.toasts.clearFailed"));
      }
    } finally {
      setSelectiveDeleting(false);
    }
  };

  // Initiate delete data - requires password confirmation first
  const handleInitiateDeleteData = () => {
    setPendingAction("delete");
    setShowConfirmPasswordModal(true);
  };

  // Handle delete my data (called after password confirmation)
  const handleDeleteMyData = async (token) => {
    if (deleteConfirmText !== "DELETE") return;

    setDeleting(true);
    try {
      const { data } = await betaAPI.deleteMyData({ confirmationToken: token });

      // Clear localStorage and redirect to login
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("selectedWorkspace");

      const summary = data?.summary;
      if (summary) {
        showToast.success(
          t("settings.account.toasts.dataDeletedSummary", {
            memberships: summary.memberships_deleted || 0,
            logs: summary.activity_logs_anonymized || 0,
          })
        );
      } else {
        showToast.success(t("settings.account.toasts.dataDeleted"));
      }
      navigate("/v2/auth/login", { replace: true });
    } catch (err) {
      console.error("Failed to delete data:", err);
      if (err.status === 403) {
        showToast.error(t("settings.account.toasts.confirmationExpired"));
        setConfirmationToken(null);
        setShowDeleteModal(false);
      } else {
        showToast.error(t("settings.account.toasts.deleteFailed"));
      }
      setDeleting(false);
    }
  };

  // Handle password confirmation for sensitive operations
  const handleConfirmPassword = async () => {
    if (!confirmPasswordValue) return;

    setConfirmingPassword(true);
    setConfirmPasswordError("");

    try {
      const { data } = await betaAPI.confirmPassword({
        password: confirmPasswordValue,
      });

      const token = data.confirmation_token;
      setConfirmationToken(token);
      setShowConfirmPasswordModal(false);
      setConfirmPasswordValue("");

      // Proceed with the pending action
      if (pendingAction === "email") {
        handleChangeEmail(token);
      } else if (pendingAction === "password") {
        // Show the password form after confirming current password
        setShowPasswordForm(true);
      } else if (pendingAction === "delete") {
        setShowDeleteModal(true);
      } else if (pendingAction === "selective-delete") {
        setShowSelectiveDeleteModal(true);
      }

      setPendingAction(null);
    } catch (err) {
      console.error("Failed to confirm password:", err);
      if (err.status === 401) {
        setConfirmPasswordError(
          t("settings.account.confirmPasswordModal.incorrect")
        );
      } else {
        setConfirmPasswordError(
          t("settings.account.confirmPasswordModal.verifyFailed")
        );
      }
    } finally {
      setConfirmingPassword(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <BetaNavbar hideWorkspaceSelector />
        <div className="flex items-center justify-center py-20">
          <Loader size={24} className="animate-spin text-[#E07A3A]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <BetaNavbar hideWorkspaceSelector />

      {/* Hero Section with gradient */}
      <div
        style={{
          background:
            "linear-gradient(180deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 50%, rgba(255, 255, 255, 0) 100%)",
        }}
      >
        <div className="mx-auto max-w-2xl px-6 pb-6 pt-8">
          {/* Page Header */}
          <div>
            <h1 className="text-xl font-bold text-[#111827]">
              {t("settings.account.title")}
            </h1>
            <p className="mt-1 text-sm text-[#6B7280]">
              {t("settings.account.subtitle")}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-6 pb-8">
        {/* Profile Section */}
        <div className="mb-6 rounded-xl border border-[#F3F4F6] bg-white">
          <div className="flex items-center justify-between border-b border-[#F3F4F6] px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FEF3E7]">
                <User size={18} className="text-[#E07A3A]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#111827]">
                  {t("settings.account.profile.title")}
                </h2>
                <p className="text-xs text-[#6B7280]">
                  {t("settings.account.profile.subtitle")}
                </p>
              </div>
            </div>
          </div>

          {!showProfileForm ? (
            /* Collapsed state */
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#111827]">
                    {profile.fname} {profile.lname}
                  </p>
                  <p className="mt-0.5 text-xs text-[#6B7280]">
                    {t("settings.account.profile.displayName")}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditedName({
                      fname: profile.fname,
                      lname: profile.lname,
                    });
                    setShowProfileForm(true);
                  }}
                  className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                >
                  {t("settings.account.profile.editName")}
                </button>
              </div>
            </div>
          ) : (
            /* Expanded state */
            <div className="space-y-4 p-6">
              {/* Name fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#374151]">
                    {t("settings.account.profile.firstName")}
                  </label>
                  <input
                    type="text"
                    value={editedName.fname}
                    onChange={(e) =>
                      setEditedName({ ...editedName, fname: e.target.value })
                    }
                    className="w-full rounded-lg border border-[#E5E7EB] px-4 py-2.5 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#E07A3A] focus:ring-2 focus:ring-[#E07A3A]/10"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#374151]">
                    {t("settings.account.profile.lastName")}
                  </label>
                  <input
                    type="text"
                    value={editedName.lname}
                    onChange={(e) =>
                      setEditedName({ ...editedName, lname: e.target.value })
                    }
                    className="w-full rounded-lg border border-[#E5E7EB] px-4 py-2.5 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#E07A3A] focus:ring-2 focus:ring-[#E07A3A]/10"
                  />
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowProfileForm(false);
                    setEditedName({ fname: "", lname: "" });
                  }}
                  className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                >
                  {t("common.actions.cancel")}
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={!hasProfileChanges || profileSaving}
                  className="rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    background: hasProfileChanges
                      ? "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)"
                      : "#E5E7EB",
                    color: hasProfileChanges ? "#fff" : "#9CA3AF",
                  }}
                >
                  {profileSaving ? (
                    <span className="flex items-center gap-2">
                      <Loader size={14} className="animate-spin" />
                      {t("settings.account.profile.saving")}
                    </span>
                  ) : (
                    t("settings.account.profile.saveChanges")
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Email Section */}
        <div className="mb-6 rounded-xl border border-[#F3F4F6] bg-white">
          <div className="flex items-center justify-between border-b border-[#F3F4F6] px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FEF3E7]">
                <Mail size={18} className="text-[#E07A3A]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#111827]">
                  {t("settings.account.email.title")}
                </h2>
                <p className="text-xs text-[#6B7280]">
                  {t("settings.account.email.subtitle")}
                </p>
              </div>
            </div>
          </div>

          {!showEmailForm ? (
            /* Collapsed state */
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#111827]">
                    {profile.email}
                  </p>
                  <p className="mt-0.5 text-xs text-[#6B7280]">
                    {t("settings.account.email.accountEmail")}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setNewEmail(profile.email);
                    setShowEmailForm(true);
                  }}
                  className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                >
                  {t("settings.account.email.changeEmail")}
                </button>
              </div>
            </div>
          ) : (
            /* Expanded state */
            <div className="space-y-4 p-6">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#374151]">
                  {t("settings.account.email.newEmailLabel")}
                </label>
                <div className="relative">
                  <Mail
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
                  />
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder={t(
                      "settings.account.email.newEmailPlaceholder"
                    )}
                    className="w-full rounded-lg border border-[#E5E7EB] py-2.5 pl-11 pr-4 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#E07A3A] focus:ring-2 focus:ring-[#E07A3A]/10"
                  />
                </div>
                {hasEmailChanges && (
                  <p className="mt-1.5 text-xs text-[#E07A3A]">
                    {t("settings.account.email.verifyNotice")}
                  </p>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowEmailForm(false);
                    setNewEmail("");
                  }}
                  className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                >
                  {t("common.actions.cancel")}
                </button>
                <button
                  onClick={handleInitiateEmailChange}
                  disabled={!hasEmailChanges || emailSaving}
                  className="rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    background: hasEmailChanges
                      ? "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)"
                      : "#E5E7EB",
                    color: hasEmailChanges ? "#fff" : "#9CA3AF",
                  }}
                >
                  {emailSaving ? (
                    <span className="flex items-center gap-2">
                      <Loader size={14} className="animate-spin" />
                      {t("settings.account.email.updating")}
                    </span>
                  ) : (
                    t("settings.account.email.updateEmail")
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Security Section */}
        <div className="mb-6 rounded-xl border border-[#F3F4F6] bg-white">
          <div className="flex items-center justify-between border-b border-[#F3F4F6] px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#EEF2FF]">
                <Shield size={18} className="text-[#6366F1]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#111827]">
                  {t("settings.account.security.title")}
                </h2>
                <p className="text-xs text-[#6B7280]">
                  {t("settings.account.security.subtitle")}
                </p>
              </div>
            </div>
          </div>

          {!showPasswordForm ? (
            /* Collapsed state - just show change password button */
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-[#111827]">
                    {t("settings.account.security.password")}
                  </h3>
                  <p className="mt-0.5 text-xs text-[#6B7280]">
                    {t("settings.account.security.lastChangedUnknown")}
                  </p>
                </div>
                <button
                  onClick={handleInitiatePasswordChange}
                  className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                >
                  {t("settings.account.security.changePassword")}
                </button>
              </div>
            </div>
          ) : (
            /* Expanded state - show password form */
            <div className="space-y-4 p-6">
              {/* New Password */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#374151]">
                  {t("settings.account.security.newPasswordLabel")}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t(
                      "settings.account.security.newPasswordPlaceholder"
                    )}
                    className="w-full rounded-lg border border-[#E5E7EB] px-4 py-2.5 pr-11 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Password requirements */}
              {newPassword.length > 0 && (
                <div className="space-y-1.5">
                  <PasswordRequirement
                    met={passwordValidation.minLength}
                    text={t("settings.account.security.reqMinLength")}
                  />
                  <PasswordRequirement
                    met={passwordValidation.hasUppercase}
                    text={t("settings.account.security.reqUppercase")}
                  />
                  <PasswordRequirement
                    met={passwordValidation.hasSymbol}
                    text={t("settings.account.security.reqSymbol")}
                  />
                </div>
              )}

              {/* Confirm Password */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#374151]">
                  {t("settings.account.security.confirmPasswordLabel")}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t(
                    "settings.account.security.confirmPasswordPlaceholder"
                  )}
                  className="w-full rounded-lg border border-[#E5E7EB] px-4 py-2.5 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/10"
                />
                {confirmPassword.length > 0 &&
                  newPassword !== confirmPassword && (
                    <p className="mt-1.5 text-xs text-[#DC2626]">
                      {t("settings.account.security.passwordsMismatch")}
                    </p>
                  )}
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowPasswordForm(false);
                    setNewPassword("");
                    setConfirmPassword("");
                    setConfirmationToken(null);
                  }}
                  className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                >
                  {t("common.actions.cancel")}
                </button>
                <button
                  onClick={() => handleChangePassword(confirmationToken)}
                  disabled={!isPasswordValid || passwordSaving}
                  className="rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    background: isPasswordValid
                      ? "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)"
                      : "#E5E7EB",
                    color: isPasswordValid ? "#fff" : "#9CA3AF",
                  }}
                >
                  {passwordSaving ? (
                    <span className="flex items-center gap-2">
                      <Loader size={14} className="animate-spin" />
                      {t("settings.account.security.changing")}
                    </span>
                  ) : (
                    t("settings.account.security.updatePassword")
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Preferences Section */}
        <div className="mb-6 rounded-xl border border-[#F3F4F6] bg-white">
          <div className="flex items-center justify-between border-b border-[#F3F4F6] px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FEF3E7]">
                <Globe size={18} className="text-[#E07A3A]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#111827]">
                  {t("settings.account.preferences.title")}
                </h2>
                <p className="text-xs text-[#6B7280]">
                  {t("settings.account.preferences.subtitle")}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium text-[#111827]">
                  {t("common.language.label")}
                </h3>
                <p className="mt-0.5 text-xs text-[#6B7280]">
                  {t("common.language.description")}
                </p>
              </div>
              <LanguageSelect
                placement="popover"
                className="w-52 flex-shrink-0"
              />
            </div>
          </div>
        </div>

        {/* Sessions */}
        <div className="mb-6 rounded-xl border border-[#F3F4F6] bg-white">
          <div className="p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium text-[#111827]">
                  {t("settings.account.sessions.title")}
                </h3>
                <p className="mt-0.5 text-xs text-[#6B7280]">
                  {t("settings.account.sessions.description")}
                </p>
              </div>
              <button
                onClick={() => setConfirmSignOutEverywhere(true)}
                className="flex flex-shrink-0 items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
              >
                <LogOut size={14} />
                {t("settings.account.sessions.button")}
              </button>
            </div>
          </div>
        </div>
        <ConfirmDialog
          open={confirmSignOutEverywhere}
          title={t("settings.account.sessions.confirmTitle")}
          body={t("settings.account.sessions.confirmBody")}
          confirmLabel={t("settings.account.sessions.button")}
          danger
          onConfirm={handleSignOutEverywhere}
          onCancel={() => setConfirmSignOutEverywhere(false)}
        />

        {/* Your Data Section */}
        <div className="mb-6 rounded-xl border border-[#F3F4F6] bg-white">
          <div className="flex items-center justify-between border-b border-[#F3F4F6] px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F0FDF4]">
                <Database size={18} className="text-[#10B981]" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-[#111827]">
                  {t("settings.account.data.title")}
                </h2>
                <p className="text-xs text-[#6B7280]">
                  {t("settings.account.data.subtitle")}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-[#111827]">
                  {t("settings.account.data.exportTitle")}
                </h3>
                <p className="mt-0.5 text-xs text-[#6B7280]">
                  {t("settings.account.data.exportDescription")}
                </p>
              </div>
              <button
                onClick={handleExportData}
                disabled={exporting}
                className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    {t("settings.account.data.exporting")}
                  </>
                ) : (
                  <>
                    <Download size={14} />
                    {t("common.actions.export")}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-[#F3F4F6]" />

          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-[#111827]">
                  {t("settings.account.data.clearTitle")}
                </h3>
                <p className="mt-0.5 text-xs text-[#6B7280]">
                  {t("settings.account.data.clearDescription")}
                </p>
              </div>
              <button
                onClick={handleInitiateSelectiveDelete}
                className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
              >
                <Trash2 size={14} />
                {t("settings.account.data.clearData")}
              </button>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="rounded-xl border border-[#FECACA] bg-white">
          <div className="flex items-center gap-3 border-b border-[#FECACA] px-6 py-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FEF2F2]">
              <AlertTriangle size={18} className="text-[#DC2626]" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-[#DC2626]">
                {t("settings.account.danger.title")}
              </h2>
              <p className="text-xs text-[#6B7280]">
                {t("settings.account.danger.subtitle")}
              </p>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-[#111827]">
                  {t("settings.account.danger.deleteTitle")}
                </h3>
                <p className="mt-0.5 text-xs text-[#6B7280]">
                  {t("settings.account.danger.deleteDescription")}
                </p>
              </div>
              <button
                onClick={handleInitiateDeleteData}
                className="rounded-lg border border-[#FECACA] bg-white px-4 py-2 text-sm font-medium text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2]"
              >
                {t("settings.account.danger.deleteTitle")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete My Data Modal */}
      <Modal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteConfirmText("");
          setConfirmationToken(null);
        }}
        maxWidth="480px"
      >
        {/* Header */}
        <div
          className="relative overflow-hidden p-7"
          style={{
            background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
          }}
        >
          {/* Decorative circles */}
          <div
            className="absolute rounded-full"
            style={{
              width: 120,
              height: 120,
              top: -30,
              right: -30,
              background: "rgba(255, 255, 255, 0.1)",
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: 80,
              height: 80,
              bottom: -40,
              right: 60,
              background: "rgba(255, 255, 255, 0.08)",
            }}
          />

          {/* Header content */}
          <div className="relative flex items-start justify-between">
            <div>
              <div
                className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ background: "rgba(255, 255, 255, 0.2)" }}
              >
                <AlertTriangle size={24} className="text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {t("settings.account.danger.deleteTitle")}
              </h2>
              <p
                className="mt-1.5 text-sm"
                style={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                {t("settings.account.deleteModal.subtitle")}
              </p>
            </div>
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmText("");
              }}
              className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-7">
          <div
            className="mb-6 flex gap-3 rounded-xl p-4"
            style={{
              background:
                "linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(220, 38, 38, 0.03) 100%)",
              border: "1px solid rgba(220, 38, 38, 0.15)",
            }}
          >
            <AlertTriangle
              size={20}
              className="mt-0.5 flex-shrink-0 text-[#DC2626]"
            />
            <div className="text-[13px] leading-relaxed text-[#6B7280]">
              <p className="mb-1 font-medium text-[#DC2626]">
                {t("settings.account.deleteModal.warning")}
              </p>
              <p>{t("settings.account.deleteModal.willPermanently")}</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                <li>{t("settings.account.deleteModal.itemAccount")}</li>
                <li>{t("settings.account.deleteModal.itemMemberships")}</li>
                <li>{t("settings.account.deleteModal.itemLogs")}</li>
                <li>{t("settings.account.deleteModal.itemInvites")}</li>
                <li>{t("settings.account.deleteModal.itemTokens")}</li>
              </ul>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              <Trans
                i18nKey="settings.account.deleteModal.typeToConfirm"
                values={{ word: "DELETE" }}
                components={{
                  bold: <span className="font-semibold text-[#111827]" />,
                }}
              />
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={t("settings.account.deleteModal.typePlaceholder", {
                word: "DELETE",
              })}
              className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#DC2626] focus:ring-2 focus:ring-[#DC2626]/10"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
          <button
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText("");
              setConfirmationToken(null);
            }}
            className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
          >
            {t("common.actions.cancel")}
          </button>
          <button
            onClick={() => handleDeleteMyData(confirmationToken)}
            disabled={deleteConfirmText !== "DELETE" || deleting}
            className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
            style={
              deleteConfirmText === "DELETE"
                ? {
                    background: "#DC2626",
                    boxShadow: "0 2px 8px rgba(220, 38, 38, 0.3)",
                    cursor: "pointer",
                  }
                : {
                    background: "#F3F4F6",
                    color: "#9CA3AF",
                    cursor: "not-allowed",
                  }
            }
          >
            {deleting ? (
              <span className="flex items-center gap-2">
                <Loader size={14} className="animate-spin" />
                {t("settings.account.deleteModal.deleting")}
              </span>
            ) : (
              t("settings.account.deleteModal.confirm")
            )}
          </button>
        </div>
      </Modal>

      {/* Selective Delete Modal */}
      <Modal
        show={showSelectiveDeleteModal}
        onClose={() => {
          setShowSelectiveDeleteModal(false);
          setSelectiveDeleteMode("all");
          setSelectiveDateFrom(null);
          setSelectiveDateTo(null);
          setConfirmationToken(null);
        }}
        maxWidth="480px"
      >
        {/* Header */}
        <div
          className="relative overflow-hidden p-7"
          style={{
            background: "linear-gradient(135deg, #D97706 0%, #B45309 100%)",
          }}
        >
          <div
            className="absolute rounded-full"
            style={{
              width: 120,
              height: 120,
              top: -30,
              right: -30,
              background: "rgba(255, 255, 255, 0.1)",
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: 80,
              height: 80,
              bottom: -40,
              right: 60,
              background: "rgba(255, 255, 255, 0.08)",
            }}
          />

          <div className="relative flex items-start justify-between">
            <div>
              <div
                className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ background: "rgba(255, 255, 255, 0.2)" }}
              >
                <Trash2 size={24} className="text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {t("settings.account.data.clearTitle")}
              </h2>
              <p
                className="mt-1.5 text-sm"
                style={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                {t("settings.account.clearModal.subtitle")}
              </p>
            </div>
            <button
              onClick={() => {
                setShowSelectiveDeleteModal(false);
                setSelectiveDeleteMode("all");
                setSelectiveDateFrom(null);
                setSelectiveDateTo(null);
                setConfirmationToken(null);
              }}
              className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-7">
          <div
            className="mb-6 flex gap-3 rounded-xl p-4"
            style={{
              background:
                "linear-gradient(135deg, rgba(217, 119, 6, 0.08) 0%, rgba(217, 119, 6, 0.03) 100%)",
              border: "1px solid rgba(217, 119, 6, 0.15)",
            }}
          >
            <AlertTriangle
              size={20}
              className="mt-0.5 flex-shrink-0 text-[#D97706]"
            />
            <div className="text-[13px] leading-relaxed text-[#6B7280]">
              <p className="mb-1 font-medium text-[#D97706]">
                {t("settings.account.clearModal.affectedTitle")}
              </p>
              <ul className="list-disc space-y-0.5 pl-4">
                <li>{t("settings.account.clearModal.itemMemberships")}</li>
                <li>{t("settings.account.clearModal.itemLogs")}</li>
                <li>{t("settings.account.clearModal.itemInvites")}</li>
                <li>{t("settings.account.clearModal.itemAccountKept")}</li>
              </ul>
            </div>
          </div>

          {/* Mode selector */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              {t("settings.account.clearModal.whatToClear")}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSelectiveDeleteMode("all")}
                className="rounded-[10px] border-2 p-3 text-left transition-all duration-150"
                style={{
                  borderColor:
                    selectiveDeleteMode === "all" ? "#D97706" : "#E5E7EB",
                  background:
                    selectiveDeleteMode === "all"
                      ? "rgba(217, 119, 6, 0.04)"
                      : "#fff",
                }}
              >
                <div className="flex items-center gap-2">
                  <Database
                    size={16}
                    className={
                      selectiveDeleteMode === "all"
                        ? "text-[#D97706]"
                        : "text-[#9CA3AF]"
                    }
                  />
                  <span
                    className={`text-sm font-medium ${
                      selectiveDeleteMode === "all"
                        ? "text-[#D97706]"
                        : "text-[#374151]"
                    }`}
                  >
                    {t("settings.account.clearModal.allData")}
                  </span>
                </div>
                <p className="mt-1 text-xs text-[#6B7280]">
                  {t("settings.account.clearModal.allDataDescription")}
                </p>
              </button>
              <button
                onClick={() => setSelectiveDeleteMode("date_range")}
                className="rounded-[10px] border-2 p-3 text-left transition-all duration-150"
                style={{
                  borderColor:
                    selectiveDeleteMode === "date_range"
                      ? "#D97706"
                      : "#E5E7EB",
                  background:
                    selectiveDeleteMode === "date_range"
                      ? "rgba(217, 119, 6, 0.04)"
                      : "#fff",
                }}
              >
                <div className="flex items-center gap-2">
                  <Calendar
                    size={16}
                    className={
                      selectiveDeleteMode === "date_range"
                        ? "text-[#D97706]"
                        : "text-[#9CA3AF]"
                    }
                  />
                  <span
                    className={`text-sm font-medium ${
                      selectiveDeleteMode === "date_range"
                        ? "text-[#D97706]"
                        : "text-[#374151]"
                    }`}
                  >
                    {t("settings.account.clearModal.dateRange")}
                  </span>
                </div>
                <p className="mt-1 text-xs text-[#6B7280]">
                  {t("settings.account.clearModal.dateRangeDescription")}
                </p>
              </button>
            </div>
          </div>

          {/* Date pickers - shown only for date_range mode */}
          {selectiveDeleteMode === "date_range" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-[#374151]">
                  {t("settings.account.clearModal.from")}
                </label>
                <DatePicker
                  value={selectiveDateFrom}
                  onChange={setSelectiveDateFrom}
                  placeholder={t("settings.account.clearModal.startDate")}
                  maxDate={new Date()}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-[#374151]">
                  {t("settings.account.clearModal.to")}
                </label>
                <DatePicker
                  value={selectiveDateTo}
                  onChange={setSelectiveDateTo}
                  placeholder={t("settings.account.clearModal.endDate")}
                  maxDate={new Date()}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
          <button
            onClick={() => {
              setShowSelectiveDeleteModal(false);
              setSelectiveDeleteMode("all");
              setSelectiveDateFrom(null);
              setSelectiveDateTo(null);
              setConfirmationToken(null);
            }}
            className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
          >
            {t("common.actions.cancel")}
          </button>
          <button
            onClick={() => handleSelectiveDelete(confirmationToken)}
            disabled={
              selectiveDeleting ||
              (selectiveDeleteMode === "date_range" &&
                (!selectiveDateFrom ||
                  !selectiveDateTo ||
                  selectiveDateFrom > selectiveDateTo))
            }
            className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
            style={
              selectiveDeleteMode === "all" ||
              (selectiveDeleteMode === "date_range" &&
                selectiveDateFrom &&
                selectiveDateTo &&
                selectiveDateFrom <= selectiveDateTo)
                ? {
                    background: "#D97706",
                    boxShadow: "0 2px 8px rgba(217, 119, 6, 0.3)",
                    cursor: selectiveDeleting ? "wait" : "pointer",
                  }
                : {
                    background: "#F3F4F6",
                    color: "#9CA3AF",
                    cursor: "not-allowed",
                  }
            }
          >
            {selectiveDeleting ? (
              <span className="flex items-center gap-2">
                <Loader size={14} className="animate-spin" />
                {t("settings.account.clearModal.clearing")}
              </span>
            ) : (
              t("settings.account.clearModal.confirm")
            )}
          </button>
        </div>
      </Modal>

      {/* Password Confirmation Modal */}
      <Modal
        show={showConfirmPasswordModal}
        onClose={() => {
          setShowConfirmPasswordModal(false);
          setConfirmPasswordValue("");
          setConfirmPasswordError("");
          setPendingAction(null);
        }}
        maxWidth="420px"
      >
        {/* Header */}
        <div
          className="relative overflow-hidden p-7"
          style={{
            background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
          }}
        >
          {/* Decorative circles */}
          <div
            className="absolute rounded-full"
            style={{
              width: 120,
              height: 120,
              top: -30,
              right: -30,
              background: "rgba(255, 255, 255, 0.1)",
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: 80,
              height: 80,
              bottom: -40,
              right: 60,
              background: "rgba(255, 255, 255, 0.08)",
            }}
          />

          {/* Header content */}
          <div className="relative flex items-start justify-between">
            <div>
              <div
                className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ background: "rgba(255, 255, 255, 0.2)" }}
              >
                <Lock size={24} className="text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {t("settings.account.confirmPasswordModal.title")}
              </h2>
              <p
                className="mt-1.5 text-sm"
                style={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                {t("settings.account.confirmPasswordModal.subtitle")}
              </p>
            </div>
            <button
              onClick={() => {
                setShowConfirmPasswordModal(false);
                setConfirmPasswordValue("");
                setConfirmPasswordError("");
                setPendingAction(null);
              }}
              className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-7">
          <p className="mb-4 text-sm text-[#6B7280]">
            {pendingAction === "email"
              ? t("settings.account.confirmPasswordModal.reasonEmail")
              : pendingAction === "password"
              ? t("settings.account.confirmPasswordModal.reasonPassword")
              : pendingAction === "selective-delete"
              ? t("settings.account.confirmPasswordModal.reasonSelectiveDelete")
              : t("settings.account.confirmPasswordModal.reasonDelete")}
          </p>

          <div>
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              {t("settings.account.confirmPasswordModal.currentPassword")}
            </label>
            <input
              type="password"
              value={confirmPasswordValue}
              onChange={(e) => {
                setConfirmPasswordValue(e.target.value);
                setConfirmPasswordError("");
              }}
              placeholder={t(
                "settings.account.confirmPasswordModal.placeholder"
              )}
              className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/10"
              onKeyDown={(e) => {
                if (e.key === "Enter" && confirmPasswordValue) {
                  handleConfirmPassword();
                }
              }}
            />
            {confirmPasswordError && (
              <p className="mt-1.5 text-xs text-[#DC2626]">
                {confirmPasswordError}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
          <button
            onClick={() => {
              setShowConfirmPasswordModal(false);
              setConfirmPasswordValue("");
              setConfirmPasswordError("");
              setPendingAction(null);
            }}
            className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
          >
            {t("common.actions.cancel")}
          </button>
          <button
            onClick={handleConfirmPassword}
            disabled={!confirmPasswordValue || confirmingPassword}
            className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
            style={
              confirmPasswordValue
                ? {
                    background:
                      "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                    boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
                    cursor: confirmingPassword ? "wait" : "pointer",
                  }
                : {
                    background: "#F3F4F6",
                    color: "#9CA3AF",
                    cursor: "not-allowed",
                  }
            }
          >
            {confirmingPassword ? (
              <span className="flex items-center gap-2">
                <Loader size={14} className="animate-spin" />
                {t("settings.account.confirmPasswordModal.verifying")}
              </span>
            ) : (
              t("common.actions.continue")
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
};

// Password requirement indicator component
const PasswordRequirement = ({ met, text }) => (
  <div className="flex items-center gap-2">
    {met ? (
      <Check size={14} className="text-[#10B981]" />
    ) : (
      <X size={14} className="text-[#9CA3AF]" />
    )}
    <span className={`text-xs ${met ? "text-[#10B981]" : "text-[#9CA3AF]"}`}>
      {text}
    </span>
  </div>
);

export default AccountSettings;
